import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api, API_BASE } from "@/api/client";
import { motion } from "framer-motion";
import { X } from "lucide-react";

export default function Gallery() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  return (
    <section id="gallery" className="py-24 md:py-32 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-primary font-bold">04 / {t.nav.gallery}</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            {t.gallery.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t.gallery.subtitle}</p>
        </div>

        {items.length === 0 ? (
          <div className="mt-14 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
            {t.gallery.empty}
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((item, i) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
                onClick={() => setSelected(item)}
                className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-card"
                data-testid={`gallery-item-${item.id}`}
              >
                <img
                  src={`${API_BASE}/gallery/file/${item.id}`}
                  alt={item.title || "Job photo"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                {item.title && (
                  <div className="absolute inset-x-0 bottom-0 p-3 text-left bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-sm font-semibold text-white">{item.title}</div>
                  </div>
                )}
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-6 right-6 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            onClick={() => setSelected(null)}
            aria-label="Close"
          >
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${API_BASE}/gallery/file/${selected.id}`}
              alt={selected.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {(selected.title || selected.description) && (
              <div className="mt-3 text-center">
                {selected.title && <div className="text-white font-semibold text-lg">{selected.title}</div>}
                {selected.description && <div className="text-neutral-300 text-sm mt-1">{selected.description}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
